// ?modal=client&client=<id> (finance redesign slice 3, DD-051): a client from Clients, in a drawer on /finance. Its four
// numbers, its Clients row's on jobs (finance consolidation S9: billed and margin each month, still due, its last
// invoice, the server's, TD-102), then its jobs to bill, done or active (answer c, 2026-10-23), then its invoices (finance-real, the user
// 2026-10-23: "the client drawer opens and shows all the invoices open and sent, the most recent at the top; clicking on
// the invoice opens the invoice modal"), in place of its months, whose drawer repeated the invoice's. A job to bill is
// the shared JobLine, opening the job's drawer in this one's place (`modal=line-job&lineJob=`, DD-045). The invoices are
// every status, drafts first, then the newest sent, each the shared InvoiceListRow (the person drawer's too): its name,
// its status-date line and its jobs, its status (the shared pill, read-only: it changes in the invoice's drawer, DD-062)
// and its amount. A row hands over to the invoice drawer (`modal=job-invoice&invoice=`, the Invoices tab's), replacing
// this one in the history (DD-045); saved rows carry no buttons. With jobs to bill, New invoice in the header stacks one
// level over it (`stack=new-invoice`, DD-045's create flow) with the done ones ticked, and making the draft hands over
// to it, as the client page does. The client's own page, where its jobs, quotes and invoices start (DD-039), is the
// arrow by its name. The agency's own client is never invoiced (TD-105): its jobs read at what their claims cost.
import type { ReactNode } from "react";
import { ArrowUpRightIcon } from "@/components/icons";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { InvoiceListRow } from "../components/InvoiceListRow";
import { JobLine } from "../components/JobLine";
import { PendingBody } from "../components/ModalPending";
import { RetryBlock } from "../components/RetryBlock";
import { NewJobInvoiceDrawer } from "../clientPage/JobInvoiceDrawers";
import { JOB_INVOICE_STATES } from "../clientPage/lineJobText";
import type { JobClientRow } from "../types";
import { invoiceDateLine, invoiceName, jobsCount, jobSubline } from "../utils/names";
import { useClientDrawer } from "./hooks/useClientDrawer";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const INVOICE = ENTITY_CONFIG.invoice;
// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobsWord = JOB.plural.toLowerCase();
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";

const NONE = <span className="text-gray-400">·</span>;

function Figure({ label, value, sub }: { label: string; value: ReactNode; sub: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-base">{value}</div>
      <div className="text-xs text-gray-400">{sub}</div>
    </div>
  );
}

interface StripFigure {
  label: string;
  value: ReactNode;
  sub: string;
}

/** The strip: the client's Clients row, figure for figure. The agency's own client is never invoiced (TD-105): its work's
 * cost alone. The margin is price less claims, before any write-off, so it is not the books' profit. */
function stripOf(row: JobClientRow): StripFigure[] {
  if (row.ownClient) {
    return [{ label: "Costs each month", value: row.monthlyMinor === 0 ? NONE : <MoneyText amountMinor={row.monthlyMinor} strong />, sub: `its recurring ${jobsWord} this period` }];
  }
  const marginSub = row.marginPct === null ? "nothing billed each month" : `${row.marginPct}% of it, after the claims`;
  const last = row.lastInvoice;
  return [
    { label: "Billed each month", value: row.monthlyMinor === 0 ? NONE : <MoneyText amountMinor={row.monthlyMinor} strong />, sub: `its recurring ${jobsWord} this period` },
    { label: "Margin each month", value: row.marginPct === null ? NONE : <MoneyText amountMinor={row.marginMonthlyMinor} strong balance />, sub: marginSub },
    { label: "Still due", value: <MoneyText amountMinor={row.dueMinor} strong />, sub: `on sent ${INVOICE.plural.toLowerCase()}` },
    {
      label: `Last ${INVOICE.label.toLowerCase()}`,
      value: last ? <MoneyText amountMinor={last.invoiceMinor} strong /> : NONE,
      sub: last ? `${invoiceName({ kind: "client", ...last })} · ${invoiceDateLine({ kind: "client", ...last })}` : "none sent yet",
    },
  ];
}

interface Props {
  clientId: string;
  onClose: () => void;
  onOpenPage: () => void;
  /** An Active job, in this drawer's place. */
  onOpenJob: (jobId: string) => void;
  /** An invoice, listed or just made, in this drawer's place. */
  onOpenInvoice: (invoiceId: string) => void;
  /** New invoice stacked over it (`stack=new-invoice`). */
  creating: boolean;
  /** The jobs New invoice ticks first (`pick=`, from To do's row); none: the ended ones. */
  picks?: string[];
  onCreate: (open: boolean) => void;
}

export function ClientDrawer({ clientId, onClose, onOpenPage, onOpenJob, onOpenInvoice, creating, picks, onCreate }: Props) {
  const { invoicesQuery, jobsQuery, rowQuery, billable } = useClientDrawer(clientId);
  const jobs = jobsQuery.data;
  const arrow = (name: string) => (
    <button type="button" onClick={onOpenPage} aria-label={`Open ${name}'s page`} className="p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 shrink-0">
      <ArrowUpRightIcon className="w-4 h-4" />
    </button>
  );
  if (!jobs) {
    return (
      <FinanceDrawer title={ENTITY_CONFIG.client.label} onClose={onClose} titleAside={arrow(`the ${clientWord}`)}>
        <PendingBody failure={jobsQuery.isError ? `Could not load this ${clientWord}.` : null} retrying={jobsQuery.isFetching} retry={() => void jobsQuery.refetch()} />
      </FinanceDrawer>
    );
  }
  // The agency's own client is never invoiced (TD-105): it reads as the client page reads it, in costs.
  const own = jobs.ownClient;
  const name = jobs.client.name;
  const canCreate = !own && billable.length > 0;
  const aside = (
    <span className="flex items-center gap-2">
      {arrow(name)}
      {/* DD-056: a header create button is an EntityCreateButton. */}
      {canCreate && <EntityCreateButton entity="invoice" variant="primary" size="sm" onClick={() => onCreate(true)} />}
    </span>
  );
  const invoices = invoicesQuery.data?.invoices ?? [];
  return (
    <>
      <FinanceDrawer title={name} onClose={onClose} titleAside={aside}>
        <div className="space-y-6">
          {rowQuery.data ? (
            <div className="grid grid-cols-2 gap-4">
              {stripOf(rowQuery.data).map((stat) => (
                <Figure key={stat.label} label={stat.label} value={stat.value} sub={stat.sub} />
              ))}
            </div>
          ) : (
            rowQuery.isError && <RetryBlock message={`Could not load ${name}'s figures.`} retrying={rowQuery.isFetching} onRetry={() => void rowQuery.refetch()} />
          )}
          <section className="text-sm">
            <h3 className={heading}>{`${JOB.plural}, done or under way`}</h3>
            {billable.length === 0 ? (
              <p className="text-gray-400">{`No ${JOB.label.toLowerCase()} done or under way for ${name}.`}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {billable.map((j) => (
                  <li key={j.id}>
                    <JobLine name={j.name} sub={jobSubline(j)} amountMinor={own ? j.claimedMinor : j.priceMinor} onOpen={() => onOpenJob(j.id)} />
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="text-sm">
            <h3 className={heading}>{INVOICE.plural}</h3>
            {own ? (
              <p className="text-gray-500">{`The agency's own ${clientWord} is never invoiced: its ${jobsWord} are costs, on its page.`}</p>
            ) : !invoicesQuery.data ? (
              <p className="text-gray-500">{invoicesQuery.isError ? invoicesQuery.error.message : `Reading ${name}'s ${INVOICE.plural.toLowerCase()}…`}</p>
            ) : invoices.length === 0 ? (
              <p className="text-gray-400">{`No ${INVOICE.plural.toLowerCase()} for ${name} yet.`}</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {invoices.map((i) => (
                  <li key={i.id}>
                    <InvoiceListRow
                      name={invoiceName({ kind: "client", ...i })}
                      dateLine={invoiceDateLine({ kind: "client", ...i })}
                      count={jobsCount(i.jobIds.length)}
                      look={JOB_INVOICE_STATES[i.status]}
                      amountMinor={i.invoiceMinor}
                      onOpen={() => onOpenInvoice(i.id)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </FinanceDrawer>
      {canCreate && creating && (
        <NewJobInvoiceDrawer
          clientId={clientId}
          titlePrefix={name}
          billable={billable}
          picks={picks}
          stacked
          onCreated={onOpenInvoice}
          onClose={() => onCreate(false)}
        />
      )}
    </>
  );
}
